/**
 * Masters Module — Core reference data catalogs.
 *
 * Single table `masters` with `type` discriminator.
 * Plugins register their own master types via the MasterRegistry service.
 * Predefined masters (countries, currencies, etc.) are seeded based on config.
 */

import type { ModuleManifest } from '@gzl10/nexus-sdk'
import { mastersEntity } from './definitions.js'
import { createMasterRegistry } from './registry.js'
import { DEFAULT_MASTER_TYPES, PREDEFINED_MASTERS } from './constants.js'
import { installTypeAction } from './actions/install-type.js'
import { uninstallTypeAction } from './actions/uninstall-type.js'

export { mastersEntity } from './definitions.js'
export { createMasterRegistry } from './registry.js'
export { DEFAULT_MASTER_TYPES, PREDEFINED_MASTERS } from './constants.js'
export type { MasterEntry, MasterRegistry } from './registry.js'

export const mastersModule: ModuleManifest = {
  name: 'masters',
  type: 'core',
  label: { en: 'Masters', es: 'Maestros' },
  icon: 'mdi:database-outline',
  description: { en: 'Reference data catalogs', es: 'Catálogos de datos de referencia' },
  category: 'settings',
  routePrefix: '/masters',
  definitions: [mastersEntity],
  actions: [installTypeAction, uninstallTypeAction],

  init: (ctx) => {
    if (!ctx.services.has('masters')) {
      ctx.services.register('masters', createMasterRegistry())
    }
  },

  seed: async (ctx) => {
    // Seed runs before init (migrations phase), ensure registry exists
    if (!ctx.services.has('masters')) {
      ctx.services.register('masters', createMasterRegistry())
    }
    const registry = ctx.services.get('masters') as ReturnType<typeof createMasterRegistry>

    // Only seed defaults if no data exists (fresh install or empty DB).
    // User data from data/seeds/masters.json is already imported by startup.
    const existing = await ctx.db.knex('masters').first()
    if (!existing) {
      // Fresh install: only seed minimal defaults (languages + timezones)
      for (const type of DEFAULT_MASTER_TYPES) {
        const loader = PREDEFINED_MASTERS[type]
        if (!loader) continue
        const entries = await loader()
        registry.register(type, entries, { seed: 'if-empty' })
      }
    }

    // Seed plugin-registered masters (always)
    await registry.seed(ctx)
  }
}
